import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconPlus, IconPencil, IconTrash, IconGripVertical } from '@tabler/icons-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { listMapLayers, deleteMapLayer, reorderMapLayers } from '../services/mapLayersService';
import type { MapLayer } from '../types';

function SortableRow({
  layer,
  onEdit,
  onDelete,
}: {
  layer: MapLayer;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: layer.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style}>
      <td style={{ width: 32, cursor: 'grab' }} {...attributes} {...listeners}>
        <IconGripVertical className="icon text-muted" />
      </td>
      <td>
        <code className="text-muted">{layer.id}</code>
      </td>
      <td>{layer.name}</td>
      <td>
        <span className="badge bg-blue-lt">{layer.category}</span>
      </td>
      <td
        className="text-muted"
        style={{
          maxWidth: 260,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={layer.url}
      >
        {layer.url}
      </td>
      <td>
        <span className={`badge text-white ${layer.enabled ? 'bg-success' : 'bg-secondary'}`}>
          {layer.enabled ? 'Enabled' : 'Disabled'}
        </span>
      </td>
      <td>
        <div className="btn-list flex-nowrap">
          <button onClick={onEdit} className="btn btn-icon" title="Edit">
            <IconPencil className="icon" />
          </button>
          <button onClick={onDelete} className="btn btn-icon text-danger" title="Delete">
            <IconTrash className="icon" />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function MapLayersPage() {
  const navigate = useNavigate();
  const [layers, setLayers] = useState<MapLayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    loadLayers();
  }, []);

  const loadLayers = async () => {
    try {
      const data = await listMapLayers();
      setLayers(data);
    } catch (error) {
      console.error('Error loading map layers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (layer: MapLayer) => {
    if (!confirm(`Delete layer "${layer.name}"? This cannot be undone.`)) return;
    try {
      await deleteMapLayer(layer.id);
      setLayers(prev => prev.filter(l => l.id !== layer.id));
    } catch (error) {
      console.error('Error deleting layer:', error);
      alert('Failed to delete layer');
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = layers.findIndex(l => l.id === active.id);
    const newIndex = layers.findIndex(l => l.id === over.id);
    const reordered = arrayMove(layers, oldIndex, newIndex);

    setLayers(reordered);

    setSaving(true);
    try {
      await reorderMapLayers(reordered);
    } catch (error) {
      console.error('Error saving order:', error);
      alert('Failed to save new order');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <div className="page-header">
      <div className="row g-2 align-items-center">
        <div className="col">
          <div className="page-pretitle">Maps</div>
          <h2 className="page-title">
            Map Layers
            {saving && <span className="spinner-border spinner-border-sm ms-2 text-muted" />}
          </h2>
        </div>
        <div className="col-auto">
          <button onClick={() => navigate('/settings/map-layers/new')} className="btn btn-primary">
            <IconPlus className="icon" />
            Add Layer
          </button>
        </div>
      </div>

      <div className="card mt-3">
        <div className="table-responsive">
          <table className="table table-vcenter card-table">
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th>ID</th>
                <th>Name</th>
                <th>Category</th>
                <th>URL</th>
                <th>Status</th>
                <th className="w-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              {layers.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty">
                      <p className="empty-title">No map layers configured</p>
                      <p className="empty-subtitle text-muted">
                        Add layers manually or run the seed script.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={layers.map(l => l.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {layers.map(layer => (
                      <SortableRow
                        key={layer.id}
                        layer={layer}
                        onEdit={() => navigate(`/settings/map-layers/${layer.id}`)}
                        onDelete={() => handleDelete(layer)}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
